/**
 * Curated, descriptive-only "org descriptor" chips a processor can select to
 * surface on their public buyer page (`/processors/:slug`). Selection is
 * stored on `LandingPageSettings.buyerChips` as an array of the stable `value`
 * codes below; the `label` is what renders.
 *
 * Deliberately NON-regulated / NON-verifiable: no "grass-fed", "organic",
 * "USDA-inspected", etc. — those are self-asserted marketing/regulatory claims
 * the platform intentionally does not let processors put on the buyer page.
 *
 * v1 starter set — the platform team should review/expand this list before a
 * wider launch. Because only the stable `value` codes are persisted, labels can
 * be reworded later without a data migration; do NOT change an existing
 * `value` (it would orphan stored selections).
 */
export interface BuyerDescriptorChip {
  /** Stable persisted code. Never change once shipped. */
  value: string;
  /** Human-facing label rendered on the buyer page + builder. */
  label: string;
}

export const BUYER_DESCRIPTOR_CHIPS: BuyerDescriptorChip[] = [
  { value: 'familyOwned', label: 'Family-owned' },
  { value: 'multiGenerational', label: 'Multi-generational' },
  { value: 'veteranOwned', label: 'Veteran-owned' },
  { value: 'womanOwned', label: 'Woman-owned' },
  { value: 'locallyOwned', label: 'Locally-owned' },
  { value: 'smallBatch', label: 'Small-batch' },
  { value: 'wholeAnimal', label: 'Whole-animal / nose-to-tail' },
  { value: 'customProcessing', label: 'Custom processing' },
  { value: 'onFarmPickup', label: 'On-farm pickup' },
  { value: 'communityFocused', label: 'Community-focused' },
];

/** The maximum number of descriptor chips a processor may select. */
export const MAX_BUYER_DESCRIPTOR_CHIPS = 5;

/** Set of valid chip codes for fast membership/validation checks. */
export const BUYER_DESCRIPTOR_CHIP_VALUES = new Set(
  BUYER_DESCRIPTOR_CHIPS.map((c) => c.value),
);

/**
 * Returns the display label for a chip code, or undefined if unknown.
 * @param value
 */
export function buyerDescriptorChipLabel(value: string): string | undefined {
  return BUYER_DESCRIPTOR_CHIPS.find((c) => c.value === value)?.label;
}

/**
 * Normalizes a raw chip selection from a client: keeps only known codes,
 * de-duplicates (preserving order), and caps at {@link MAX_BUYER_DESCRIPTOR_CHIPS}.
 * Shared by the upsert mutation (authoritative) so callers can't persist
 * invalid, duplicate, or over-cap selections.
 * @param chips
 */
export function sanitizeBuyerDescriptorChips(
  chips: readonly string[] | null | undefined,
): string[] {
  if (!chips) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const c of chips) {
    if (BUYER_DESCRIPTOR_CHIP_VALUES.has(c) && !seen.has(c)) {
      seen.add(c);
      result.push(c);
    }
    if (result.length >= MAX_BUYER_DESCRIPTOR_CHIPS) break;
  }
  return result;
}
